import { CronExpressionParser } from "cron-parser";
import React from "react";
import { Alert, Card, Col, Container, Image, Row } from "react-bootstrap";
import Countdown from "react-countdown";
import { useLocation } from "react-router";

import * as api from "../../api/api";
import {
    DroneConfigDocument,
    useDroneConfigDocument,
    useLocationDocument,
    useRoutineDocument,
} from "../../api/firestoreWrappers.generated";
import {
    useDroneMissionsRtdbDocument,
    useDroneWidgetsRtdbDocument,
} from "../../api/rtdbWrappers.generated";
import { Title } from "../../components/Layout/Layout";
import MissionList from "../../components/MissionList";
import { useUser } from "../../contexts/user_provider";
import { useDroneOnline } from "../../hooks/useDroneOnline";
import { DroneStatus } from "../DronePage";
import { LivestreamComponent } from "../LivestreamPage";

const ROUTINE_TIME_OFFSET = 0; // seconds to go live after routine starts

function getDate(interval, timezone) {
    const intervalIterator = CronExpressionParser.parse(interval, {
        tz: timezone,
    });
    return intervalIterator.next().toDate();
}

export default function DemoPage() {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const organizationId = queryParams.get("organizationId");
    const locationId = queryParams.get("locationId");
    const routineId = queryParams.get("routineId");
    const [routine] = useRoutineDocument(organizationId, locationId, routineId);
    const [locationDoc] = useLocationDocument(organizationId, locationId, {
        mode: "once",
    });
    const timezone = locationDoc?.timezone || "UTC";
    const [currentTime, setCurrentTime] = React.useState(undefined);
    const droneId = routine?.droneId;
    const [droneWidgets] = useDroneWidgetsRtdbDocument(droneId);
    const [droneConfig] = useDroneConfigDocument(droneId);
    const [activeMissions] = useDroneMissionsRtdbDocument(droneId);
    const hasActiveMission = (activeMissions || []).length > 0;
    const [isDemoActive, setIsDemoActive] = React.useState(false);
    const [missions] = api.useDroneMissions(
        organizationId,
        locationId,
        droneId,
        3,
    );
    const { getUserIsAdmin } = useUser();
    const isDroneOnline = useDroneOnline(droneId);
    const payloadStreamEnabled = droneConfig?.streamer?.enable_payload_stream;

    const renderer = ({ days, hours, minutes, seconds, completed }) => {
        return (
            <>
                {completed && "Starting"}
                {days > 0 && `${days}d `}
                {hours > 0 && `${hours}h `}
                {minutes > 0 && `${minutes}m `}
                {seconds > 0 && `${seconds}s `}
            </>
        );
    };

    const CorvusLogo = ({ width, height, className }) => {
        return (
            <Image
                src="/CorvusLogo.png"
                width={width}
                height={height}
                className={className}
                alt="Corvus Drones logo"
            />
        );
    };

    React.useEffect(() => {
        if (payloadStreamEnabled === false && isDemoActive) {
            DroneConfigDocument(droneId).update({
                "streamer.enable_payload_stream": true,
            });
        }
    }, [payloadStreamEnabled, droneId, isDemoActive]);

    React.useEffect(() => {
        if (routine?.interval) {
            const date = getDate(routine.interval, timezone);
            date.setSeconds(date.getSeconds() + ROUTINE_TIME_OFFSET);
            setCurrentTime(date);
        }
    }, [routine, timezone]);

    React.useEffect(() => {
        if (hasActiveMission) {
            setIsDemoActive(true);
            return;
        }

        if (!isDemoActive) {
            return;
        }

        const timeout = setTimeout(() => {
            setIsDemoActive(false);
        }, 5000);

        return () => {
            clearTimeout(timeout);
        };
    }, [hasActiveMission, isDemoActive]);

    if (!organizationId || !locationId || !routineId) {
        return (
            <Container style={{ maxWidth: "850px" }} className="mb-5 mt-4">
                <Card>
                    <Card.Body className="p-4 pt-0 mt-4">
                        No organizationId, locationId and/or routineId. Access
                        this page from a routine page.
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    if (!routine) {
        return <div>Loading...</div>;
    }

    return (
        <Container style={{ maxWidth: "850px" }} className="mb-5 mt-4">
            <Card>
                <title>Demo | MyCorvus</title>
                {getUserIsAdmin() && (
                    <>
                        {isDemoActive && (
                            <Title title={`Drone ${droneId}`}>
                                <CorvusLogo
                                    height="50px"
                                    className={"d-block ms-sm-auto"}
                                />
                            </Title>
                        )}
                        <Card.Body className="p-4 pt-0 mt-0">
                            {isDemoActive && (
                                <>
                                    {isDroneOnline && (
                                        <>
                                            {droneWidgets &&
                                                Object.keys(droneWidgets)
                                                    .length > 0 && (
                                                    <div className="mb-4">
                                                        <DroneStatus
                                                            droneStatus={
                                                                droneWidgets.status
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            {droneId && (
                                                <LivestreamComponent
                                                    droneId={droneId}
                                                    cameraName="Payload"
                                                />
                                            )}
                                            <MissionList
                                                className="mt-4"
                                                organizationId={organizationId}
                                                locationId={locationId}
                                                missions={missions}
                                            />
                                        </>
                                    )}
                                    {!isDroneOnline && (
                                        <Alert
                                            className="text-center mb-0"
                                            variant="secondary"
                                        >
                                            <div className="text-muted">
                                                Drone is offline
                                            </div>
                                        </Alert>
                                    )}
                                </>
                            )}
                            {!isDemoActive && (
                                <Container
                                    fluid
                                    className="d-flex flex-column justify-content-center align-items-center text-center mt-4"
                                >
                                    <Row>
                                        <Col>
                                            <CorvusLogo
                                                width="40%"
                                                className={"mb-4"}
                                            />
                                            {routine.enabled && (
                                                <>
                                                    <h1 className="display-1">
                                                        <b>Next demo</b>
                                                    </h1>
                                                    <code className="display-2">
                                                        {currentTime && (
                                                            <Countdown
                                                                date={
                                                                    currentTime
                                                                }
                                                                key={
                                                                    currentTime
                                                                }
                                                                renderer={
                                                                    renderer
                                                                }
                                                                autoStart={true}
                                                                intervalDelay={
                                                                    0
                                                                }
                                                                onComplete={() => {
                                                                    setIsDemoActive(
                                                                        true,
                                                                    );
                                                                    // Wait 1 second before updating the current time to ensure we set it to the next interval
                                                                    setTimeout(
                                                                        () => {
                                                                            const date =
                                                                                getDate(
                                                                                    routine.interval,
                                                                                    timezone,
                                                                                );
                                                                            date.setSeconds(
                                                                                date.getSeconds() +
                                                                                    ROUTINE_TIME_OFFSET,
                                                                            );
                                                                            setCurrentTime(
                                                                                date,
                                                                            );
                                                                        },
                                                                        1000,
                                                                    );
                                                                }}
                                                            />
                                                        )}
                                                    </code>
                                                </>
                                            )}
                                            {!routine.enabled && (
                                                <>
                                                    <h1 className="display-1">
                                                        <b>Next demo</b>
                                                    </h1>
                                                    <code className="display-2">
                                                        not scheduled
                                                    </code>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                </Container>
                            )}
                        </Card.Body>
                    </>
                )}
                {!getUserIsAdmin() && (
                    <Card.Body>
                        <Alert variant="danger">
                            This page can only be accessed with administrator
                            rights
                        </Alert>
                    </Card.Body>
                )}
            </Card>
        </Container>
    );
}
